import { useTranslation } from 'react-i18next'
import type { ReactElement } from 'react'
import { WifiHigh, Toilet, CheckCircle } from '@phosphor-icons/react'
import {
  FACILITY_CATALOG,
  type FacilityKey,
  facilityAvailability,
  accessibilityFacts,
} from './facilityCatalog'
import facBridge from './assets/fac-bridge.svg'
import facPayphones from './assets/fac-payphones.svg'
import facHelpPoints from './assets/fac-help-points.svg'
import facEscalators from './assets/fac-escalators.svg'
import facWaitingRoom from './assets/fac-waiting-room.svg'
import facLifts from './assets/fac-lifts.svg'
import accWarning from './assets/acc-warning.svg'
import styles from './StationFacilitiesSection.module.css'

interface Props {
  /** Raw facility codes from schema.json for the station. */
  facilities: readonly string[]
}

/**
 * Figma-exported tile icons; Wi-Fi and Toilets are SF Symbols in the
 * design (forbidden on web) — Phosphor stand-ins, same precedent as
 * the Overview's disrupted-service icons.
 */
const FACILITY_ICONS: Record<FacilityKey, string | 'wifi' | 'toilet'> = {
  BRIDGE: facBridge,
  WIFI: 'wifi',
  TOILETS: 'toilet',
  PAYPHONES: facPayphones,
  HELPPOINTS: facHelpPoints,
  ESCALATORS: facEscalators,
  WAITINGROOM: facWaitingRoom,
  LIFTS: facLifts,
}

function tileIcon(key: FacilityKey): ReactElement {
  const icon = FACILITY_ICONS[key]
  if (icon === 'wifi') return <WifiHigh size={20} weight="bold" aria-hidden />
  if (icon === 'toilet') return <Toilet size={20} weight="fill" aria-hidden />
  return <img src={icon} alt="" width={20} height={20} />
}

/**
 * Station facilities + Accessibility — Figma 8597:41774 (TASK-699).
 * Left: the fixed facility catalog; codes present in the schema render
 * available, the rest greyed with an "Unavailable" subtext (the design
 * shows both states). Right: step-free headline derived from the codes
 * and the Escalators / Lifts / Accessible toilet rows. The design's
 * "Open step-free map" link is deliberately hidden (removed 2026-08-04
 * per user — nothing sensible to open yet).
 */
export function StationFacilitiesSection({ facilities }: Props): ReactElement {
  const { t } = useTranslation('station-page')
  const availability = facilityAvailability(facilities)
  const access = accessibilityFacts(facilities)

  const accessibilityRows = [
    { label: t('access_row_escalators'), available: access.escalators },
    { label: t('access_row_lifts'), available: access.lifts },
    { label: t('access_row_accessible_toilet'), available: access.accessibleToilet },
  ]

  return (
    <div className={styles.section}>
      <div className={styles.grid}>
        <div>
          <p className={styles.caption}>{t('facilities_caption')}</p>
          <ul className={styles.facilities}>
            {FACILITY_CATALOG.map(({ key, labelKey }) => {
              const available = availability[key]
              return (
                <li key={key} className={available ? styles.facility : styles.facilityUnavailable}>
                  <span className={styles.tile}>{tileIcon(key)}</span>
                  <span className={styles.facilityText}>
                    <span className={styles.facilityLabel}>{t(labelKey)}</span>
                    {!available && (
                      <span className={styles.facilitySubtext}>{t('facility_unavailable')}</span>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
        <div>
          <p className={styles.caption}>{t('accessibility_caption')}</p>
          <div className={styles.stepFree}>
            {access.stepFree ? (
              // Design ships only the warning variant; the positive state
              // reuses the layout with a Phosphor stand-in check.
              <CheckCircle size={24} weight="fill" className={styles.stepFreeCheck} aria-hidden />
            ) : (
              <img src={accWarning} alt="" width={24} height={24} />
            )}
            <span className={styles.stepFreeText}>
              <span className={styles.stepFreeTitle}>
                {access.stepFree ? t('access_step_free_yes_title') : t('access_step_free_no_title')}
              </span>
              <span className={styles.stepFreeNote}>
                {access.stepFree ? t('access_step_free_yes_note') : t('access_step_free_no_note')}
              </span>
            </span>
          </div>
          <dl className={styles.accessRows}>
            {accessibilityRows.map(({ label, available }) => (
              <div key={label} className={styles.accessRow}>
                <dt className={styles.accessLabel}>{label}</dt>
                <dd className={available ? styles.accessAvailable : styles.accessUnavailable}>
                  {available ? t('access_available') : t('access_unavailable')}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  )
}
